import React from 'react';
import { Box, Text, Button, HStack } from '@chakra-ui/react';
import { FiCheck, FiX } from 'react-icons/fi';

/**
 * Banner for custom field preview with approve/reject buttons.
 * Displays count of entities with preview data.
 *
 * variant "inline" = bar below header (legacy).
 * variant "floating" = glass card over chat panel (web3 minimalist).
 *
 * @param {Object} props
 * @param {number} props.previewCount - Number of entities with preview data
 * @param {string} props.entityType - Type of entity ('companies' | 'contacts' | 'sec_filings')
 * @param {Function} props.onApprove - Approve handler
 * @param {Function} props.onReject - Reject handler
 * @param {string} [props.variant='inline'] - 'inline' | 'floating'
 */
const ENTITY_LABELS = {
  companies: { singular: 'company', plural: 'companies' },
  contacts: { singular: 'contact', plural: 'contacts' },
  sec_filings: { singular: 'filing', plural: 'filings' },
};

const CustomFieldPreviewBanner = ({
  previewCount,
  entityType,
  onApprove,
  onReject,
  variant = 'inline',
}) => {
  const labels = ENTITY_LABELS[entityType] || { singular: 'row', plural: 'rows' };
  const displayLabel = previewCount === 1 ? labels.singular : labels.plural;

  if (previewCount === 0) return null;

  const isFloating = variant === 'floating';

  const primaryBtn = {
    bg: '#fafafa',
    color: '#0a0a0a',
    borderRadius: 'full',
    fontWeight: '600',
    fontSize: '12px',
    h: '28px',
    px: 3,
    _hover: { opacity: 0.85 },
  };

  const outlineBtn = {
    bg: 'transparent',
    borderRadius: 'full',
    fontWeight: '500',
    fontSize: '12px',
    h: '28px',
    px: 3,
    border: '1px solid rgba(255,255,255,0.15)',
    color: 'rgba(255,255,255,0.5)',
    _hover: {
      borderColor: 'rgba(255,255,255,0.3)',
      color: 'rgba(255,255,255,0.85)',
      bg: 'rgba(255,255,255,0.04)',
    },
  };

  if (isFloating) {
    return (
      <Box
        position="relative"
        left={0}
        right={0}
        zIndex={10}
        p={3}
        borderRadius="10px"
        mx={3}
        mb={2}
        bg="#111111"
        border="1px solid rgba(255,255,255,0.10)"
        boxShadow="0 8px 32px rgba(0,0,0,0.4)"
        backdropFilter="blur(12px)"
        sx={{ WebkitBackdropFilter: 'blur(12px)' }}
        display="flex"
        flexDirection="column"
        gap={3}
      >
        <Box>
          <Text fontSize="sm" color="#fafafa" fontWeight="600" mb={1}>
            Apply to all rows or discard.
          </Text>
          <Text fontSize="sm" color="#a3a3a3" fontWeight="400">
            Preview: {previewCount} {displayLabel} updated with new properties.
          </Text>
        </Box>
        <Box display="flex" justifyContent="flex-end">
          <HStack spacing={2} flexShrink={0}>
            <Button
              {...outlineBtn}
              leftIcon={<FiX size={13} strokeWidth={2.25} />}
              onClick={onReject}
            >
              Reject
            </Button>
            <Button
              {...primaryBtn}
              leftIcon={<FiCheck size={13} strokeWidth={2.25} />}
              onClick={onApprove}
            >
              Run
            </Button>
          </HStack>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      bg="#111111"
      borderBottom="1px solid"
      borderColor="rgba(255,255,255,0.10)"
      px={4}
      py={3}
      display="flex"
      alignItems="center"
      gap={3}
    >
      <Text fontSize="sm" color="#a3a3a3" fontWeight="500" flex={1}>
        Preview: {previewCount} {displayLabel} with new custom fields
      </Text>
      <HStack spacing={2}>
        <Button
          {...outlineBtn}
          leftIcon={<FiX size={13} strokeWidth={2.25} />}
          onClick={onReject}
        >
          Reject
        </Button>
        <Button
          {...primaryBtn}
          leftIcon={<FiCheck size={13} strokeWidth={2.25} />}
          onClick={onApprove}
        >
          Run for All
        </Button>
      </HStack>
    </Box>
  );
};

export default CustomFieldPreviewBanner;
